import React, { useEffect } from "react";
import {
  Select,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  SelectProps,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchGeneriqueDataByType } from "../../reduxStore/GeneriqueDataSlice";
import { RootState, AppDispatch } from "../../reduxStore/Store";
import { GeneriqueDataType } from "../../constants/enums";

interface SelectCategorieProps
  extends Omit<SelectProps<number | string>, "value" | "onChange"> {
  /** Type "catégorie" à lister (ex: CATEGORIE_NATURE_REGLEMENT, CATEGORIE_DEPOT). */
  categoryType: GeneriqueDataType;
  /** Catégorie sélectionnée (parent_id de l'entité dans la table `famille`). */
  value: number | null;
  /** null = aucune catégorie (entité non catégorisée). */
  onChange: (categorieId: number | null) => void;
  /** Libellé du champ. Défaut: 'Catégorie'. */
  label?: string;
}

/**
 * Select générique d'une catégorie (generique_data d'un type "catégorie").
 * Réutilisable pour tout type catégorisable du registre CATEGORISABLE_TYPES.
 */
const SelectCategorie: React.FC<SelectCategorieProps> = ({
  categoryType,
  value,
  onChange,
  label = "Catégorie",
  ...selectProps
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { byType, loading } = useSelector((state: RootState) => state.GeneriqueDataSlice);
  const options = byType[categoryType] ?? [];

  useEffect(() => {
    dispatch(fetchGeneriqueDataByType({ type: categoryType }));
  }, [dispatch, categoryType]);

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    const v = event.target.value;
    onChange(v === "" || v == null ? null : Number(v));
  };

  if (loading && options.length === 0) {
    return <CircularProgress size={20} />;
  }

  return (
    <FormControl fullWidth size="small" variant="outlined">
      <InputLabel id={`select-categorie-${categoryType}-label`}>{label}</InputLabel>
      <Select
        labelId={`select-categorie-${categoryType}-label`}
        value={value ?? ""}
        onChange={handleChange}
        label={label}
        size="small"
        {...selectProps}
      >
        <MenuItem value="">
          <em>Aucune (non catégorisée)</em>
        </MenuItem>
        {options.map((cat) => (
          <MenuItem key={cat.id} value={cat.id}>
            {cat.nom}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SelectCategorie;
